import React from "react";
import { Logo } from "../../Assets.tsx";
import FacebookIcon from "@mui/icons-material/Facebook";
import InstagramIcon from "@mui/icons-material/Instagram";
import LinkedInIcon from "@mui/icons-material/LinkedIn";
import { useHistory } from "react-router-dom";
import gsap from "gsap";
import { useGSAP } from "@gsap/react";
import { ScrollTrigger } from "gsap/ScrollTrigger";
gsap.registerPlugin(ScrollTrigger);

/* Theme : same as Our Products page.
           navy #064081 | blue #0a66c2 | tint #e6eef8 | light blue #7fb4ea
   Icons : white stroke on blue tiles (WhatsApp keeps its green). */

const STYLES = `
@import url("https://fonts.googleapis.com/css2?family=Open+Sans:wght@400;600&family=Ubuntu:wght@700&display=swap");

.ft{--head:#4573a6;--btn:#2596be;--sky:#a9d3e6;--tint:#eef6fa;
  position:relative;overflow:hidden;color:#fff;font-family:"Open Sans",sans-serif;font-weight:400;
  background:linear-gradient(165deg,#1d3049 0%,#2f4d75 55%,#4573a6 100%)}
.ft::before{content:"";position:absolute;inset:0;pointer-events:none;
  background-image:radial-gradient(rgba(255,255,255,.08) 1.2px,transparent 1.2px);background-size:26px 26px;
  -webkit-mask-image:linear-gradient(180deg,#000,transparent 75%);mask-image:linear-gradient(180deg,#000,transparent 75%)}
.ft-orb{position:absolute;border-radius:50%;pointer-events:none;filter:blur(80px)}
.ft-orb.a{width:460px;height:460px;top:-160px;right:-140px;background:rgba(37,150,190,.35)}
.ft-orb.b{width:360px;height:360px;bottom:-140px;left:-120px;background:rgba(169,211,230,.16)}
.ft>*{position:relative;z-index:1}

.ft-wave{line-height:0}
.ft-wave svg{display:block;width:100%;height:56px}

.ft-wrap{max-width:1240px;margin:0 auto;padding:44px 28px 34px}

/* ---------- CTA band ---------- */
.ft-cta{position:relative;overflow:hidden;display:flex;align-items:center;justify-content:space-between;gap:24px;flex-wrap:wrap;
  padding:30px 36px;margin-bottom:56px;border-radius:28px;
  background:linear-gradient(120deg,rgba(255,255,255,.1),rgba(255,255,255,.04));border:1px solid rgba(255,255,255,.16);backdrop-filter:blur(10px);
  box-shadow:0 30px 60px -34px rgba(0,0,0,.7)}
.ft-cta::after{content:"";position:absolute;width:260px;height:260px;border-radius:50%;right:-80px;top:-120px;background:rgba(169,211,230,.25);filter:blur(50px);pointer-events:none}
/* heading = Ubuntu Bold, like OurProducts headings */
.ft-cta h3{margin:0 0 6px;font-family:"Ubuntu",sans-serif;font-weight:700;font-size:26px;letter-spacing:.6px;line-height:1.25;color:#fff}
/* body = Open Sans 14.5px */
.ft-cta p{margin:0;font-family:"Open Sans",sans-serif;font-weight:400;font-size:14.5px;line-height:1.75;color:rgba(255,255,255,.8)}
/* button = same as .op-btn */
.ft-cta-btn{position:relative;overflow:hidden;z-index:0;display:inline-flex;align-items:center;gap:8px;padding:12px 28px;border:none;border-radius:50px;
  background:#fff;color:var(--head);font-family:"Open Sans",sans-serif;font-size:14px;font-weight:600;cursor:pointer;
  transition:color .35s,transform .35s cubic-bezier(.3,1.6,.5,1),box-shadow .35s}
.ft-cta-btn::before{content:"";position:absolute;inset:0;z-index:-1;background:linear-gradient(90deg,var(--head),var(--btn));
  transform:translateX(-101%);transition:transform .45s cubic-bezier(.2,.8,.2,1)}
.ft-cta-btn:hover{color:#fff;transform:translateY(-3px);box-shadow:0 18px 30px -12px rgba(0,0,0,.6)}
.ft-cta-btn:hover::before{transform:none}
.ft-cta-btn span{display:inline-block;transition:transform .35s}
.ft-cta-btn:hover span{transform:translateX(5px)}

/* ---------- GRID ---------- */
.ft-grid{display:grid;grid-template-columns:1.3fr 1fr 1fr 1.4fr;gap:44px;align-items:start}

/* brand */
.ft-logo{display:inline-block;cursor:pointer;padding:8px 12px;border-radius:16px;background:rgba(255,255,255,.07);border:1px solid rgba(255,255,255,.18);
  transition:transform .4s cubic-bezier(.3,1.6,.5,1),box-shadow .4s}
.ft-logo:hover{transform:translateY(-4px);box-shadow:0 18px 30px -14px rgba(37,150,190,.8)}
.ft-logo img{display:block;width:150px;padding:4px 8px;border-radius:8px;background:#fff}
.ft-brand{display:block;margin-top:16px;font-family:"Ubuntu",sans-serif;font-size:17px;font-weight:700;letter-spacing:.6px;text-transform:uppercase;line-height:1.35;
  background:linear-gradient(90deg,#fff 25%,var(--sky) 50%,#fff 75%);background-size:200% auto;
  -webkit-background-clip:text;background-clip:text;-webkit-text-fill-color:transparent;animation:ftShimmer 4s linear infinite}
/* tagline = chip style (Open Sans 400, 12px, 2px spacing, uppercase) */
.ft-tag{margin:8px 0 18px;font-family:"Open Sans",sans-serif;font-weight:400;font-size:12px;letter-spacing:2px;text-transform:uppercase;color:rgba(255,255,255,.65)}
.ft-social{display:flex;gap:10px}
.ft-soc{width:44px;height:44px;border-radius:14px;display:flex;align-items:center;justify-content:center;cursor:pointer;
  background:rgba(255,255,255,.07);border:1px solid rgba(255,255,255,.18);
  transition:background .35s,border-color .35s,transform .4s cubic-bezier(.3,1.6,.5,1),box-shadow .35s}
.ft-soc:hover{background:linear-gradient(135deg,var(--head),var(--btn));border-color:transparent;transform:translateY(-5px) rotate(-6deg);
  box-shadow:0 14px 24px -10px rgba(37,150,190,.9)}

/* titles = card h3 (Ubuntu Bold 20px, .6px) */
.ft-title{position:relative;margin:0 0 22px;padding-bottom:12px;font-family:"Ubuntu",sans-serif;font-size:20px;font-weight:700;letter-spacing:.6px;color:var(--sky)}
.ft-title::after{content:"";position:absolute;left:0;bottom:0;height:3px;width:30px;border-radius:3px;background:linear-gradient(90deg,var(--head),var(--btn));
  transition:width .45s cubic-bezier(.2,.8,.2,1)}
.ft-col:hover .ft-title::after{width:60px}
/* links = body text (Open Sans 14.5px) */
.ft-link{display:flex;align-items:center;gap:8px;padding:7px 0;font-family:"Open Sans",sans-serif;font-weight:400;font-size:14.5px;color:rgba(255,255,255,.78);cursor:pointer;
  transition:color .3s,padding-left .35s cubic-bezier(.2,.8,.2,1)}
.ft-link::before{content:"→";color:var(--sky);font-size:14px;opacity:0;width:0;transition:opacity .3s,width .3s}
.ft-link:hover{color:#fff;padding-left:4px}
.ft-link:hover::before{opacity:1;width:16px}

/* contact rows */
.ft-row{display:flex;align-items:center;gap:14px;padding:11px 0;border-bottom:1px solid rgba(255,255,255,.1)}
.ft-row:last-child{border-bottom:none}
.ft-ico{flex:none;width:42px;height:42px;border-radius:13px;display:flex;align-items:center;justify-content:center;
  background:linear-gradient(135deg,var(--head),var(--btn));box-shadow:0 10px 18px -10px rgba(37,150,190,.9);
  transition:transform .45s cubic-bezier(.3,1.6,.5,1)}
.ft-ico.ft-wapp{background:#fff}
.ft-row:hover .ft-ico{transform:rotate(-8deg) scale(1.1)}
.ft-lab{font-family:"Open Sans",sans-serif;font-weight:400;font-size:12px;letter-spacing:2px;text-transform:uppercase;color:rgba(255,255,255,.6);margin-bottom:2px}
.ft-val{font-family:"Open Sans",sans-serif;font-weight:400;font-size:14.5px;color:rgba(255,255,255,.9);cursor:pointer;transition:color .3s;word-break:break-word}
.ft-val:hover{color:var(--sky)}

/* ---------- BOTTOM ---------- */
.ft-line{height:1px;background:linear-gradient(90deg,transparent,rgba(169,211,230,.4),transparent)}
.ft-bottom{max-width:1240px;margin:0 auto;padding:22px 28px;display:flex;align-items:center;justify-content:space-between;gap:14px;flex-wrap:wrap;
  font-family:"Open Sans",sans-serif;font-weight:400;font-size:14px;color:rgba(255,255,255,.7)}
.ft-addr{display:flex;align-items:center;gap:8px;cursor:pointer;transition:color .3s}
.ft-addr:hover{color:var(--sky)}
.ft-policy{display:flex;gap:22px}
.ft-policy span{cursor:pointer;position:relative;transition:color .3s}
.ft-policy span::after{content:"";position:absolute;left:0;bottom:-3px;height:2px;width:0;background:var(--sky);transition:width .35s}
.ft-policy span:hover{color:#fff}
.ft-policy span:hover::after{width:100%}

@keyframes ftShimmer{0%{background-position:-200% center}100%{background-position:200% center}}

@media(max-width:1000px){
  .ft-grid{grid-template-columns:repeat(2,minmax(0,1fr));gap:40px}
}
@media(max-width:600px){
  .ft-wrap{padding:36px 18px 26px}
  .ft-cta{padding:24px 20px;margin-bottom:40px}.ft-cta h3{font-size:21px}
  .ft-grid{grid-template-columns:1fr;gap:34px}
  .ft-bottom{flex-direction:column;text-align:center;padding:20px 18px}
  .ft-policy{justify-content:center}
}
@media(prefers-reduced-motion:reduce){
  .ft *,.ft *::before,.ft *::after{animation:none!important;transition:none!important}
}
`;

const WhatsAppIcon = () => (
  <svg width="22" height="22" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path
      d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413Z"
      fill="#25D366"
    />
  </svg>
);

const PhoneIcon = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M22 16.92v3a2 2 0 01-2.18 2 19.79 19.79 0 01-8.63-3.07A19.5 19.5 0 014.9 13.88 19.79 19.79 0 011.88 5.26 2 2 0 013.88 3h3a2 2 0 012 1.72c.127.96.361 1.903.7 2.81a2 2 0 01-.45 2.11L8.09 10.91a16 16 0 006 6l1.27-1.27a2 2 0 012.11-.45c.907.339 1.85.573 2.81.7A2 2 0 0122 17.92z" />
  </svg>
);

const MailIcon = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <rect x="2" y="4" width="20" height="16" rx="3" />
    <path d="M2 7l10 7 10-7" />
  </svg>
);

const PinIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#7fb4ea" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M12 21s7-6.2 7-11.5A7 7 0 005 9.5C5 14.8 12 21 12 21z" />
    <circle cx="12" cy="9.5" r="2.5" />
  </svg>
);

const Footer = () => {
  const currentYear = new Date().getFullYear();
  const history = useHistory();

  const handleEmail2 = () => window.open("mailto:atul.w@theatum.com");
  const handlePhone = () => window.open("tel:9890521717");
  const handleWhatsApp = () => window.open("https://wa.me/+918080411453", "_blank");
  const handleFacebook = () => window.open("https://www.facebook.com/profile.php?id=61578474913924", "_blank");
  const handleInsagram = () => window.open("https://www.instagram.com/atuminfotech/", "_blank");
  const handleLinkedIn = () => window.open("https://www.linkedin.com/company/106907834/", "_blank");
  const handleMap = () => window.open("https://maps.app.goo.gl/upfe796tcdbdB8p9A", "_blank");

  const handleLinkClick = (path: string) => history.push(path);

  const services = [
    { label: "Web Development", path: "/website-development-services" },
    { label: "Full Stack", path: "/fullstack-service" },
    { label: "Mobile App", path: "/Mobile-App-service" },
    { label: "E-Commerce", path: "/E-Commerce-Service" },
    { label: "Web Design", path: "/WebDesign-service" },
    { label: "Digital Marketing", path: "/digital-Marketing" },
  ];

  const explore = [
    { label: "Services", path: "/Service" },
    { label: "Products", path: "/Products" },
    { label: "Portfolio", path: "/Portfolio" },
    { label: "About Us", path: "/aboutUs" },
    { label: "Hire Expert", path: "/" },
    { label: "Contact", path: "/contact" },
  ];

  useGSAP(() => {
    gsap.from(".gsapFooterLogo", {
      opacity: 0,
      duration: 0.8,
      scale: 0.5,
      y: 30,
      delay: 0.2,
      scrollTrigger: {
        trigger: ".gsapFooterLogo",
        scroller: "body",
        start: "top 90%",
      },
    });
    gsap.from(".FooterSection", {
      y: 40,
      opacity: 0,
      duration: 0.7,
      stagger: 0.15,
      delay: 0.2,
      scrollTrigger: {
        trigger: ".FooterSection",
        scroller: "body",
        start: "top 90%",
      },
    });
  });

  return (
    <div className="ft">
      <style>{STYLES}</style>

      {/* <div className="ft-orb a" /> */}
      {/* <div className="ft-orb b" /> */}

      {/* wave (fill matches the page bg above) */}
      <div className="ft-wave">
        <svg viewBox="0 0 1440 56" xmlns="http://www.w3.org/2000/svg" preserveAspectRatio="none">
          <path d="M0,28 C360,56 1080,0 1440,28 L1440,0 L0,0 Z" fill="#f5f8fc" />
        </svg>
      </div>

      <div className="ft-wrap">
        {/* CTA band */}
        <div className="ft-cta FooterSection">
          <div>
            <h3>Have a project in mind?</h3>
            <p>Let’s build your next product together.</p>
          </div>
          <button className="ft-cta-btn" onClick={() => history.push("/contact")}>
            Get in touch <span>→</span>
          </button>
        </div>

        <div className="ft-grid">
          {/* Brand */}
          <div className="ft-col FooterSection">
            <div className="ft-logo gsapFooterLogo" onClick={() => history.push("/")}>
              <img src={Logo} alt="ATUM Logo" />
            </div>
            <span className="ft-brand">ATUM Information Technologies</span>
            <p className="ft-tag">Building Tomorrow's Digital Future</p>

            <div className="ft-social">
              <div className="ft-soc" onClick={handleFacebook}>
                <FacebookIcon style={{ fontSize: 24, color: "#fff" }} />
              </div>
              <div className="ft-soc" onClick={handleInsagram}>
                <InstagramIcon style={{ fontSize: 24, color: "#fff" }} />
              </div>
              <div className="ft-soc" onClick={handleLinkedIn}>
                <LinkedInIcon style={{ fontSize: 24, color: "#fff" }} />
              </div>
            </div>
          </div>

          {/* Services */}
          <div className="ft-col FooterSection">
            <div className="ft-title">Services</div>
            {services.map((s, i) => (
              <div key={i} className="ft-link" onClick={() => handleLinkClick(s.path)}>
                {s.label}
              </div>
            ))}
          </div>

          {/* Explore */}
          <div className="ft-col FooterSection">
            <div className="ft-title">Explore More</div>
            {explore.map((e, i) => (
              <div key={i} className="ft-link" onClick={() => handleLinkClick(e.path)}>
                {e.label}
              </div>
            ))}
          </div>

          {/* Contact */}
          <div className="ft-col FooterSection">
            <div className="ft-title">Contact Us</div>

            <div className="ft-row">
              <div className="ft-ico"><PhoneIcon /></div>
              <div>
                <div className="ft-lab">Call Us</div>
                <span className="ft-val" onClick={handlePhone}>+91 9890521717</span>
              </div>
            </div>

            <div className="ft-row">
<div className="ft-ico ft-wapp"><WhatsAppIcon /></div>
              <div>
                <div className="ft-lab">WhatsApp</div>
                <span className="ft-val" onClick={handleWhatsApp}>+91 8080411453</span>
              </div>
            </div>

            <div className="ft-row">
              <div className="ft-ico"><MailIcon /></div>
              <div>
                <div className="ft-lab">Email</div>
                <span className="ft-val" onClick={handleEmail2}>atul.w@theatum.com</span>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="ft-line" />

      <div className="ft-bottom">
        <div className="ft-addr" onClick={handleMap}>
          <PinIcon />
          18, TRIOS, Symphony IT Park, Nanded City, Pune - 411024
        </div>

        <div>{currentYear} © ATUM Information Technologies LLP. All rights reserved.</div>

        <div className="ft-policy">
          <span>Privacy Policy</span>
          <span>Terms of Use</span>
        </div>
      </div>
    </div>
  );
};

export default Footer;